// apps/web/src/lib/off.ts
//
// Direct Open Food Facts fetch from the BROWSER. The server can't do this
// reliably — Cloudflare Worker egress IPs are shared datacentre IPs that OFF
// rate-limits/blocks. The user's device IP is fine, and OFF v2 sends
// `Access-Control-Allow-Origin: *`, so a direct browser fetch works.
import { offProductUrl, offProductUrlV0, codeVariants, normalizeOff, normalizeOffProduct, OFF_HOST, OFF_FIELDS } from "@shared/off-normalize";
import type { NormalizedProduct } from "@shared/types";

const TIMEOUT_MS = 6000;

export class OffFetchError extends Error {
  constructor(public reason: "network" | "http" | "json", public detail?: unknown) {
    super(`off ${reason}`);
  }
}

/**
 * Returns the normalised product on hit, `null` on a genuine miss (OFF
 * status:0 / unusable data). Throws `OffFetchError` on transport failure so
 * the caller can distinguish "not in DB" (cache a miss, go to AI) from
 * "couldn't reach OFF" (don't cache, go to AI).
 */
async function fetchJson(url: string): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { headers: { Accept: "application/json" }, signal: ctrl.signal });
  } catch (e) {
    throw new OffFetchError("network", (e as Error).message);
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchProductFromOff(code: string): Promise<NormalizedProduct | null> {
  // v2 is strict about the exact stored code form, so a scan tries each
  // plausible form, then the lenient v0 endpoint. Found products cost one
  // request as before — the ladder only runs on misses. A 404 means "this
  // form isn't stored" (keep trying); any other failure is transport and
  // throws, so a rate-limit or outage is never cached as a miss.
  const urls = [...codeVariants(code).map(offProductUrl), offProductUrlV0(code)];
  for (const url of urls) {
    const res = await fetchJson(url);
    if (res.status === 404) continue;
    if (!res.ok) throw new OffFetchError("http", res.status);
    let body: unknown;
    try {
      body = await res.json();
    } catch (e) {
      throw new OffFetchError("json", (e as Error).message);
    }
    const product = normalizeOff(code, body);
    if (product) return product;
    // status:0 body (v0's way of saying miss) — keep trying remaining forms.
  }
  return null;
}

const SEARCH_TIMEOUT_MS = 6000;

/** OFF v2 text-search URL (browser-side; same host/CORS as the product fetch). */
export function searchOffUrl(query: string): string {
  return `${OFF_HOST}/api/v2/search?search_terms=${encodeURIComponent(query)}&fields=${OFF_FIELDS}&page_size=20`;
}

/**
 * Browser-side OFF text search. Returns normalised products (unusable entries
 * dropped). Throws `OffFetchError` on transport failure so the caller can show
 * an error state. Honors an external `signal` (aborts the in-flight fetch).
 */
export async function searchOff(query: string, signal?: AbortSignal): Promise<NormalizedProduct[]> {
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  if (signal) signal.addEventListener("abort", onAbort, { once: true });
  const timer = setTimeout(() => ctrl.abort(), SEARCH_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(searchOffUrl(query), { headers: { Accept: "application/json" }, signal: ctrl.signal });
  } catch (e) {
    throw new OffFetchError("network", (e as Error).message);
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onAbort);
  }
  if (!res.ok) throw new OffFetchError("http", res.status);

  let body: unknown;
  try {
    body = await res.json();
  } catch (e) {
    throw new OffFetchError("json", (e as Error).message);
  }
  const products: unknown[] = Array.isArray((body as { products?: unknown[] })?.products)
    ? (body as { products: unknown[] }).products
    : [];
  return products
    .map((p) => normalizeOffProduct(String((p as { code?: unknown })?.code ?? ""), p as never))
    .filter((x): x is NormalizedProduct => x !== null);
}
